import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { ChevronLeft, FileDown, CheckCircle, XCircle, FileSignature, Pencil } from 'lucide-react';
import { devisApi, contratsApi } from '../../utils/api';
import SignatureButton from '../../components/Signature/SignatureButton';
import EnvoiDocumentButton from '../../components/EnvoiDocumentButton';
import PiecesJointes from '../../components/PiecesJointes';
import { getStatutBadgeClass, getStatutLabel, formatMontant } from '../../utils/calculs';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';

const DAYS_LABELS = {
  lundi: 'Lundi', mardi: 'Mardi', mercredi: 'Mercredi', jeudi: 'Jeudi', vendredi: 'Vendredi',
};

export default function DevisDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const autoEnvoyer = searchParams.get('envoyer') === '1';
  const [devis, setDevis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    devisApi.getById(id)
      .then(res => setDevis(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [id]);

  const formatDate = (d) => {
    if (!d) return '—';
    try { return format(parseISO(d), 'd MMMM yyyy', { locale: fr }); }
    catch { return d; }
  };

  const handleStatut = async (statut) => {
    setUpdating(true);
    try {
      await devisApi.updateStatut(id, statut);
      setDevis(prev => ({ ...prev, statut }));
    } catch (err) {
      alert('Erreur');
    } finally {
      setUpdating(false);
    }
  };

  const handleConvertToContrat = async () => {
    if (!window.confirm('Convertir ce devis en contrat actif ?')) return;
    setUpdating(true);
    try {
      await contratsApi.create({
        devis_id: devis.id,
        enfant_id: devis.enfant_id,
        structure_id: devis.structure_id,
        date_debut: devis.date_debut_contrat,
        heures_hebdo: devis.heures_hebdo,
        tarif_horaire: devis.tarif_horaire,
        montant_mensuel: devis.montant_mensuel,
      });
      navigate('/contrats');
    } catch (err) {
      alert('Erreur lors de la création du contrat');
    } finally {
      setUpdating(false);
    }
  };

  if (loading) return <div className="py-16 text-center text-gray-400">Chargement...</div>;
  if (!devis) return <div className="py-16 text-center text-gray-400">Devis non trouvé</div>;

  const schedule = ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi']
    .filter(d => devis[`${d}_debut`] && devis[`${d}_fin`]);

  return (
    <div className="space-y-5 max-w-3xl">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/devis')} className="btn-secondary">
            <ChevronLeft size={16} /> Retour
          </button>
          <h2 className="text-xl font-bold text-gray-900">
            Devis {devis.numero ? devis.numero : `#${String(devis.id).padStart(4, '0')}`}
          </h2>
          <span className={getStatutBadgeClass(devis.statut)}>{getStatutLabel(devis.statut)}</span>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <a
            href={devisApi.getPdfUrl(devis.id)}
            target="_blank"
            rel="noreferrer"
            className="btn-primary"
            title="Génère le devis d'accueil avec toutes les mentions légales"
          >
            <FileDown size={15} /> Émettre le devis (PDF)
          </a>
          <EnvoiDocumentButton type="devis" refId={devis.id} enfantId={devis.enfant_id} destinataireDefaut={devis.parent1_email} label="Envoyer par mail" autoOpen={autoEnvoyer}
            messageDefaut={`Bonjour,\n\nVeuillez trouver ci-joint le devis d'accueil${devis.enfant_prenom ? ` pour ${devis.enfant_prenom}${devis.enfant_nom ? ' ' + devis.enfant_nom : ''}` : ''}${devis.structure_nom ? ` au sein de ${devis.structure_nom}` : ''}.\n\nCe devis récapitule les modalités d'accueil, le tarif mensuel (lissé sur l'année) et, le cas échéant, l'estimation de l'aide CMG versée par la CAF. Il est valable 30 jours.\n\nPour réserver la place, il convient de nous retourner le devis daté et signé (mention « Bon pour accord »), accompagné des frais de dossier.\n\nNous restons à votre disposition pour toute question.\n\nBien cordialement,\n${devis.structure_nom ? `L'équipe de ${devis.structure_nom}` : 'Les Cachettes'}`} />
          {devis.statut !== 'accepte' && (
            <Link to={`/devis/${devis.id}/modifier`} className="btn-secondary" title="Modifier ce devis (tant qu'il n'est pas accepté)">
              <Pencil size={15} /> Modifier
            </Link>
          )}
          <SignatureButton type="devis" refId={devis.id} nom={`${devis.enfant_prenom || ''} ${devis.enfant_nom || ''}`.trim()} email={devis.parent1_email} />

          {devis.statut === 'en_cours' && (
            <>
              <button
                onClick={() => handleStatut('accepte')}
                disabled={updating}
                className="inline-flex items-center gap-2 px-3 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg text-sm font-medium transition-colors"
              >
                <CheckCircle size={15} /> Accepter
              </button>
              <button
                onClick={() => handleStatut('refuse')}
                disabled={updating}
                className="inline-flex items-center gap-2 px-3 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg text-sm font-medium transition-colors"
              >
                <XCircle size={15} /> Refuser
              </button>
            </>
          )}
          {devis.statut === 'accepte' && (
            <button
              onClick={handleConvertToContrat}
              disabled={updating}
              className="btn-primary"
            >
              <FileSignature size={15} /> Créer le contrat
            </button>
          )}
        </div>
      </div>

      {/* Info grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="card p-5">
          <h3 className="font-semibold text-gray-700 mb-3">Informations</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-500">Enfant</span>
              {devis.enfant_id ? (
                <Link to={`/enfants/${devis.enfant_id}`} className="font-medium text-primary-600 hover:underline">
                  {devis.enfant_prenom} {devis.enfant_nom}
                </Link>
              ) : <span>—</span>}
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Structure</span>
              <span className="font-medium">{devis.structure_nom}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Date du devis</span>
              <span className="font-medium">{formatDate(devis.date_devis)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Début de contrat</span>
              <span className="font-medium">{formatDate(devis.date_debut_contrat)}</span>
            </div>
            {devis.parent1_nom && (
              <div className="flex justify-between">
                <span className="text-gray-500">Parent</span>
                <span className="font-medium">{devis.parent1_prenom} {devis.parent1_nom}</span>
              </div>
            )}
          </div>
        </div>

        <div className="card p-5 bg-primary-50 border-primary-100">
          <h3 className="font-semibold text-primary-700 mb-3">Récapitulatif financier</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-primary-600">Heures/semaine</span>
              <span className="font-medium text-gray-800">{(devis.heures_hebdo || 0).toFixed(2)}h</span>
            </div>
            <div className="flex justify-between">
              <span className="text-primary-600">Heures lissées/mois</span>
              <span className="font-medium text-gray-800">{(devis.heures_lissees || 0).toFixed(2)}h</span>
            </div>
            <div className="flex justify-between">
              <span className="text-primary-600">Tarif horaire</span>
              <span className="font-medium text-gray-800">{formatMontant(devis.tarif_horaire)}/h</span>
            </div>
            <div className="flex justify-between pt-2 border-t border-primary-200">
              <span className="font-bold text-primary-700">Mensualité</span>
              <span className="font-bold text-xl text-primary-600">{formatMontant(devis.montant_mensuel)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-primary-600">Arrhes (1 mois)</span>
              <span className="font-medium text-gray-800">{formatMontant(devis.montant_mensuel)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Schedule */}
      {schedule.length > 0 && (
        <div className="card p-5">
          <h3 className="font-semibold text-gray-700 mb-3">Planning</h3>
          <div className="space-y-2">
            {['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi'].map(day => {
              const debut = devis[`${day}_debut`];
              const fin = devis[`${day}_fin`];
              if (!debut || !fin) return null;
              const duree = parseFloat(fin.split(':')[0]) + parseFloat(fin.split(':')[1]) / 60
                - parseFloat(debut.split(':')[0]) - parseFloat(debut.split(':')[1]) / 60;
              return (
                <div key={day} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                  <span className="text-sm font-medium text-gray-700 w-24">{DAYS_LABELS[day]}</span>
                  <span className="text-sm text-gray-600">{debut} → {fin}</span>
                  <span className="text-sm text-primary-600 font-medium">{duree.toFixed(1)}h</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Notes */}
      {devis.notes && (
        <div className="card p-5">
          <h3 className="font-semibold text-gray-700 mb-2">Notes</h3>
          <p className="text-sm text-gray-600 whitespace-pre-line">{devis.notes}</p>
        </div>
      )}

      {/* Statut actions */}
      <div className="card p-5">
        <h3 className="font-semibold text-gray-700 mb-3">Changer le statut</h3>
        <div className="flex flex-wrap gap-2">
          {['en_cours', 'accepte', 'refuse', 'expire'].map(s => (
            <button
              key={s}
              onClick={() => handleStatut(s)}
              disabled={updating || devis.statut === s}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                devis.statut === s
                  ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                  : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'
              }`}
            >
              {getStatutLabel(s)}
            </button>
          ))}
        </div>
      </div>

      <PiecesJointes entiteType="devis" entiteId={devis.id} title="Pièces jointes du devis" />
    </div>
  );
}
